import { observable, IObservableArray, flow, toJS } from 'mobx';
import API from 'api';
import Query from 'utilities/Query';
import * as yup from 'yup';
import { action } from 'mobx/lib/mobx';
import PrescriberPractice from 'stores/PrescriberPractice';

export default class Prescriber {
	@observable id: number;
	@observable prescriberNo: string = '';
	@observable prescriberName: string = '';
	@observable prescriberSurname: string = '';
	@observable prescriberType: string = '';
	@observable prescriberPhone: string = '';
	@observable prescriberMobile: string = '';
	@observable prescriberEmail: string = '';
	@observable prescriberFAX: string = '';
	@observable prescriberTitle: string = '';
	@observable created: string;
	@observable modified: string;
	@observable api: boolean = false;
	@observable disabled: boolean = false;
	@observable PrescriberPractices: IObservableArray<PrescriberPractice> = [];

	constructor(data: JSON = null) {
		if (data) this.extend(data);
	}

	extend(data: JSON) {
		Object.assign(this, data);
	}

	static ValidationSchemaPrescriber = yup.object().shape({
		prescriberName: yup
			.string()
			.label('First Name')
			.required(),
		prescriberSurname: yup
			.string()
			.label('Last Name')
			.required(),
		prescriberNo: yup
			.number()
			.label('prescriber Number')
			.required(),
		prescriberType: yup
			.string()
			.label('Prescriber Type')
			.required(),
		prescriberEmail: yup
			.string()
			.email()
			.label('Email Address'),
		prescriberMobile: yup
			.string()
			.nullable()
			.matches(/^[0-9]*$/, {
				message: 'Only numeric characters are allowed for Mobile',
				excludeEmptyString: true
			}),
		disabled: yup
			.boolean()
			.label('Disabled')
			.required()
	});

	static loadById = async (id: number): Promise<Prescriber> => {
		let res = await API.get(
			`/Prescribers/${id}?filter=${JSON.stringify({
				include: [{ PrescriberPractices: ['Practice', 'PrescriberPracticeContacts'] }]
			})}`
		);
		return new Prescriber(res);
	};

	@action
	reload = async () => {
		let res = await Prescriber.loadById(this.id);
		this.extend(toJS(res));
	};

	update = flow(function*(withData) {
		if (withData) this.extend(withData);
		yield Prescriber.ValidationSchemaPrescriber.validate(toJS(this), {
			abortEarly: false
		});
		if (this.PrescriberPractices.filter(pp => !pp.isDeleted).length === 0) {
			throw Error('One Practice link is Mandatory.');
		}
		this.modified = new Date();
		if (this.id) {
			// Update record
			yield API.patch(`/Prescribers/${this.id}`, toJS(this));
		} else {
			// New record
			this.created = new Date();
			let result = yield API.post(`/Prescribers`, toJS(this));
			this.id = result.id;
		}
		yield Promise.all(
			this.PrescriberPractices.map(pp => {
				pp.prescriberID = this.id;
				return new PrescriberPractice(pp).update();
			})
		);
	});

	static loadPrescribers(query?: Query): Promise<{ itemCount: number, items: Array<Prescriber> }> {
		if (!query) {
			query = new Query();
			query.pageSize = 1000;
		}
		return new Promise((resolve, reject) => {
			API.get(`/Prescribers/prescribers/${query.toToLpQueryUrl()}`)
				.then(result => {
					let res = {
						itemCount: result.count,
						items: result.items.map(el => new Prescriber(el))
					};
					resolve(res);
				})
				.catch(err => {
					reject(err);
				});
		});
	}

	static loadByPrescriberNumber = async (prescriberNo: string): Promise<Array<Prescriber>> => {
		return await API.get(
			`/Prescribers?filter=${JSON.stringify({
				where: { and: [{ prescriberNo: prescriberNo }, { disabled: false }] }
			})}`
		);
	};
}



// WEBPACK FOOTER //
// ./src/stores/Prescriber.js